import { useCallback, useEffect, useState } from "react";

export type AdminHashPanelAction = "create" | "edit" | "view" | "share";

export type AdminHashPanelState = {
  action: AdminHashPanelAction;
  resource: string;
  id?: string | null;
};

export type AdminHashFragmentState = {
  panelState: AdminHashPanelState | null;
  extras: URLSearchParams;
  bareExtraKeys?: ReadonlySet<string>;
  routePath?: string;
};

function encodeAdminHashSegment(value: string) {
  return encodeURIComponent(String(value)).replace(/-/g, "%2D");
}

function decodeAdminHashSegment(value: string) {
  return decodeURIComponent(String(value || ""));
}

function normalizeHashInput(hash: string) {
  return String(hash || "")
    .replace(/^#/, "")
    .trim();
}

function extractAdminPanelToken(normalizedHash: string) {
  if (!normalizedHash) {
    return null;
  }
  const tokens = normalizedHash
    .split("&")
    .map((token) => token.trim())
    .filter(Boolean);
  const matchedToken = tokens.find((token) => /^(create|edit|view|share)-.+$/.test(token));
  return matchedToken || null;
}

export function parseAdminHashPanel(hash: string): AdminHashPanelState | null {
  const normalized = normalizeHashInput(hash);
  const panelToken = extractAdminPanelToken(normalized);
  if (!panelToken) {
    return null;
  }
  const actionMatch = panelToken.match(/^(create|edit|view|share)-(.+)$/);
  if (!actionMatch) {
    return null;
  }
  const action = actionMatch[1] as AdminHashPanelAction;
  const remainder = actionMatch[2];
  if (!remainder) {
    return null;
  }

  if (action === "create") {
    return {
      action,
      resource: remainder,
      id: null,
    };
  }

  const splitIndex = remainder.lastIndexOf("-");
  if (splitIndex <= 0 || splitIndex >= remainder.length - 1) {
    return null;
  }

  return {
    action,
    resource: remainder.slice(0, splitIndex),
    id: decodeAdminHashSegment(remainder.slice(splitIndex + 1)),
  };
}

export function formatAdminHashPanel(state: AdminHashPanelState) {
  const suffix = state.id ? `-${encodeAdminHashSegment(String(state.id))}` : "";
  return `#${state.action}-${state.resource}${suffix}`;
}

export function parseAdminHashFragment(hash: string): AdminHashFragmentState {
  const normalized = normalizeHashInput(hash);
  const tokens = normalized
    .split("&")
    .map((token) => token.trim())
    .filter(Boolean);
  const extras = new URLSearchParams();
  const bareExtraKeys = new Set<string>();
  const routePath = tokens[0]?.startsWith("/") ? tokens.shift() : undefined;
  let panelState: AdminHashPanelState | null = null;
  tokens.forEach((token) => {
    if (!panelState && /^(create|edit|view|share)-.+$/.test(token)) {
      panelState = parseAdminHashPanel(`#${token}`);
      return;
    }
    const separatorIndex = token.indexOf("=");
    if (separatorIndex <= 0) {
      const key = decodeURIComponent(token);
      extras.append(key, "");
      bareExtraKeys.add(key);
      return;
    }
    const key = token.slice(0, separatorIndex);
    const value = token.slice(separatorIndex + 1);
    extras.append(decodeURIComponent(key), decodeURIComponent(value));
  });
  return {
    panelState,
    extras,
    ...(bareExtraKeys.size > 0 ? { bareExtraKeys } : {}),
    ...(routePath ? { routePath } : {}),
  };
}

export function formatAdminHashFragment(fragment: AdminHashFragmentState) {
  const parts: string[] = [];
  if (fragment.routePath) {
    parts.push(fragment.routePath);
  }
  fragment.extras.forEach((value, key) => {
    if (value === "" && fragment.bareExtraKeys?.has(key)) {
      parts.push(encodeURIComponent(key));
      return;
    }
    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
  });
  if (fragment.panelState) {
    parts.push(formatAdminHashPanel(fragment.panelState).replace(/^#/, ""));
  }
  return parts.length > 0 ? `#${parts.join("&")}` : "";
}

export function replaceAdminHashPanel(state: AdminHashPanelState | null) {
  if (typeof window === "undefined") {
    return;
  }
  const baseUrl = `${window.location.pathname}${window.location.search}`;
  const current = parseAdminHashFragment(window.location.hash);
  const hash = formatAdminHashFragment({
    panelState: state,
    extras: current.extras,
    ...(current.bareExtraKeys ? { bareExtraKeys: current.bareExtraKeys } : {}),
    ...(current.routePath ? { routePath: current.routePath } : {}),
  });
  window.history.replaceState(null, "", `${baseUrl}${hash}`);
}

function installAdminHashPanelHistorySync() {
  if (typeof window === "undefined") {
    return;
  }
  const historyWithSync = window.history as History & {
    __adminHashPanelSyncInstalled?: boolean;
  };
  if (historyWithSync.__adminHashPanelSyncInstalled) {
    return;
  }
  const dispatchLocationChange = () => {
    window.dispatchEvent(new Event("admin-hash-panel-location-change"));
  };
  const wrapHistoryMethod = (method: "pushState" | "replaceState") => {
    const original = window.history[method];
    window.history[method] = function patchedHistoryMethod(this: History, ...args) {
      const result = original.apply(this, args);
      dispatchLocationChange();
      return result;
    } as History[typeof method];
  };
  wrapHistoryMethod("pushState");
  wrapHistoryMethod("replaceState");
  historyWithSync.__adminHashPanelSyncInstalled = true;
}

export function useAdminHashPanelState() {
  const [panelState, setPanelState] = useState<AdminHashPanelState | null>(() =>
    typeof window !== "undefined" ? parseAdminHashPanel(window.location.hash) : null,
  );

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }
    const sync = () => {
      setPanelState(parseAdminHashPanel(window.location.hash));
    };
    installAdminHashPanelHistorySync();
    sync();
    window.addEventListener("hashchange", sync);
    window.addEventListener("admin-hash-panel-location-change", sync);
    return () => {
      window.removeEventListener("hashchange", sync);
      window.removeEventListener("admin-hash-panel-location-change", sync);
    };
  }, []);

  const openPanel = useCallback((state: AdminHashPanelState) => {
    replaceAdminHashPanel(state);
    setPanelState(state);
  }, []);

  const closePanel = useCallback(
    (resource?: string) => {
      const current =
        typeof window !== "undefined" ? parseAdminHashPanel(window.location.hash) : panelState;
      if (!resource || current?.resource === resource) {
        replaceAdminHashPanel(null);
        setPanelState(null);
      }
    },
    [panelState],
  );

  return {
    panelState,
    openPanel,
    closePanel,
  };
}
